import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { imgSrc } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-[#E0D5C7] text-[#2B231D] placeholder-[#A89C8F] focus:outline-none focus:border-[#9C6D3B] text-sm transition-colors";
const labelCls = "block text-xs font-semibold text-[#594E44] mb-1.5";

const ImageField = ({ label, value, onChange, testid }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads/site`, fd, { withCredentials: true });
      onChange(data.path);
      toast.success("Foto geüpload");
    } catch (err) {
      toast.error("Upload mislukt", { description: err.response?.data?.detail || "Probeer een andere afbeelding." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={imgSrc(value)} alt={label} className="w-16 h-16 rounded-xl object-cover border border-[#E0D5C7]" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-[#F2EDE4] border border-[#E0D5C7]" />
        )}
        <button
          type="button"
          data-testid={testid}
          onClick={() => ref.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2B231D] text-[#F9F6F0] text-xs font-medium hover:bg-[#43352B] transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploaden..." : "Nieuwe foto"}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
};

const Sectie = ({ titel, children }) => (
  <div className="bg-white rounded-3xl border border-[#EAE3DA] shadow-sm p-6 sm:p-8 space-y-5">
    <h2 className="text-xl font-display font-bold text-[#2B231D]">{titel}</h2>
    {children}
  </div>
);

export const ContentEditor = ({ content, onSaved }) => {
  const [form, setForm] = useState(content);
  const [saving, setSaving] = useState(false);

  const setHero = (k, v) => setForm({ ...form, hero: { ...form.hero, [k]: v } });
  const setStory = (k, v) => setForm({ ...form, story: { ...form.story, [k]: v } });
  const setGalleryItem = (i, k, v) => {
    const gallery = form.gallery.map((item, idx) => (idx === i ? { ...item, [k]: v } : item));
    setForm({ ...form, gallery });
  };
  const setListItem = (lijst, i, k, v) => {
    const nieuw = form[lijst].map((item, idx) => (idx === i ? { ...item, [k]: v } : item));
    setForm({ ...form, [lijst]: nieuw });
  };
  const setWorkshop = (k, v) => setForm({ ...form, workshop: { ...(form.workshop || {}), [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, form, { withCredentials: true });
      toast.success("Wijzigingen opgeslagen", { description: "De website is direct bijgewerkt." });
      onSaved();
    } catch (err) {
      toast.error("Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="content-editor">
      <Sectie titel="Hero (bovenaan de pagina)">
        <div>
          <label className={labelCls}>Label boven de titel</label>
          <input data-testid="edit-hero-badge" className={inputCls} value={form.hero.badge} onChange={(e) => setHero("badge", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Hoofdtitel</label>
          <input data-testid="edit-hero-title" className={inputCls} value={form.hero.title} onChange={(e) => setHero("title", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Introductietekst</label>
          <textarea data-testid="edit-hero-subtitle" rows="3" className={inputCls} value={form.hero.subtitle} onChange={(e) => setHero("subtitle", e.target.value)} />
        </div>
        <ImageField label="Achtergrondfoto" testid="edit-hero-image-upload" value={form.hero.image} onChange={(v) => setHero("image", v)} />
      </Sectie>

      <Sectie titel="Over René">
        <div>
          <label className={labelCls}>Kopje boven de titel</label>
          <input data-testid="edit-story-kicker" className={inputCls} value={form.story.kicker} onChange={(e) => setStory("kicker", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Titel</label>
          <input data-testid="edit-story-title" className={inputCls} value={form.story.title} onChange={(e) => setStory("title", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Eerste alinea</label>
          <textarea data-testid="edit-story-p1" rows="4" className={inputCls} value={form.story.p1} onChange={(e) => setStory("p1", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Tweede alinea</label>
          <textarea data-testid="edit-story-p2" rows="4" className={inputCls} value={form.story.p2} onChange={(e) => setStory("p2", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Quote op de foto</label>
          <input data-testid="edit-story-quote" className={inputCls} value={form.story.quote} onChange={(e) => setStory("quote", e.target.value)} />
        </div>
        <ImageField label="Foto van René / werkplaats" testid="edit-story-image-upload" value={form.story.image} onChange={(v) => setStory("image", v)} />
      </Sectie>

      {form.gallery.map((item, i) => (
        <Sectie key={i} titel={`Werkstuk ${i + 1}: ${item.title}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Badge (label op de foto)</label>
              <input data-testid={`edit-gallery-${i}-badge`} className={inputCls} value={item.badge} onChange={(e) => setGalleryItem(i, "badge", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Titel</label>
              <input data-testid={`edit-gallery-${i}-title`} className={inputCls} value={item.title} onChange={(e) => setGalleryItem(i, "title", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Beschrijving</label>
            <textarea data-testid={`edit-gallery-${i}-text`} rows="3" className={inputCls} value={item.text} onChange={(e) => setGalleryItem(i, "text", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Detail links (bijv. materiaal)</label>
              <input data-testid={`edit-gallery-${i}-meta`} className={inputCls} value={item.meta} onChange={(e) => setGalleryItem(i, "meta", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Detail rechts (bijv. type werk)</label>
              <input data-testid={`edit-gallery-${i}-tag`} className={inputCls} value={item.tag} onChange={(e) => setGalleryItem(i, "tag", e.target.value)} />
            </div>
          </div>
          <ImageField label="Foto van het werkstuk" testid={`edit-gallery-${i}-image-upload`} value={item.image} onChange={(v) => setGalleryItem(i, "image", v)} />
        </Sectie>
      ))}

      <Sectie titel="Werkplaats & Regio">
        <ImageField
          label="Foto bij Werkplaats & Regio"
          testid="edit-workshop-image-upload"
          value={form.workshop?.image || "/assets/mood.jpg"}
          onChange={(v) => setWorkshop("image", v)}
        />
      </Sectie>

      <Sectie titel="Werkwijze (4 stappen)">
        {form.process.map((s, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-5 pb-5 border-b border-[#F0EBE3] last:border-0 last:pb-0">
            <div>
              <label className={labelCls}>Stap {i + 1} — titel</label>
              <input data-testid={`edit-process-${i}-title`} className={inputCls} value={s.title} onChange={(e) => setListItem("process", i, "title", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Stap {i + 1} — tekst</label>
              <textarea data-testid={`edit-process-${i}-text`} rows="2" className={inputCls} value={s.text} onChange={(e) => setListItem("process", i, "text", e.target.value)} />
            </div>
          </div>
        ))}
      </Sectie>

      <Sectie titel="Ervaringen (3 quotes)">
        {form.testimonials.map((t, i) => (
          <div key={i} className="space-y-4 pb-5 border-b border-[#F0EBE3] last:border-0 last:pb-0">
            <div>
              <label className={labelCls}>Quote {i + 1}</label>
              <textarea data-testid={`edit-testimonial-${i}-quote`} rows="3" className={inputCls} value={t.quote} onChange={(e) => setListItem("testimonials", i, "quote", e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Naam / aanduiding</label>
                <input data-testid={`edit-testimonial-${i}-naam`} className={inputCls} value={t.naam} onChange={(e) => setListItem("testimonials", i, "naam", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Project</label>
                <input data-testid={`edit-testimonial-${i}-project`} className={inputCls} value={t.project} onChange={(e) => setListItem("testimonials", i, "project", e.target.value)} />
              </div>
            </div>
          </div>
        ))}
      </Sectie>

      <Sectie titel="Veelgestelde vragen">
        {form.faq.map((v, i) => (
          <div key={i} className="space-y-4 pb-5 border-b border-[#F0EBE3] last:border-0 last:pb-0">
            <div>
              <label className={labelCls}>Vraag {i + 1}</label>
              <input data-testid={`edit-faq-${i}-vraag`} className={inputCls} value={v.vraag} onChange={(e) => setListItem("faq", i, "vraag", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Antwoord {i + 1}</label>
              <textarea data-testid={`edit-faq-${i}-antwoord`} rows="3" className={inputCls} value={v.antwoord} onChange={(e) => setListItem("faq", i, "antwoord", e.target.value)} />
            </div>
          </div>
        ))}
      </Sectie>

      <div className="sticky bottom-6 flex justify-end">
        <button
          type="button"
          data-testid="content-save-button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#9C6D3B] text-white font-bold text-sm hover:bg-[#7F5830] transition-colors shadow-lg disabled:opacity-60"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Alles opslaan
        </button>
      </div>
    </div>
  );
};
